import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Smartphone, CheckCircle, XCircle, RefreshCw, LogOut, X, Search, Database, CreditCard, AlertTriangle } from 'lucide-react';
import { authUtils } from '../utils/auth';  // ✅ ADD THIS

const API_URL = '/api';

// ✅ Setup axios to include auth token
const axiosInstance = axios.create({
  baseURL: API_URL
});

axiosInstance.interceptors.request.use((config) => {
  const token = authUtils.getToken();  // ✅ USE authUtils!
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function WhatsAppConnect() {
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState({ isConnected: false, hasQR: false, isConnecting: false });
  const [loading, setLoading] = useState(false);
  const [checkNumber, setCheckNumber] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [connectionError, setConnectionError] = useState(null); // ✅ NEW

  // Bulk check state
  const [bulkStats, setBulkStats] = useState({ unchecked: 0, valid: 0, invalid: 0, total: 0 });
  const [bulkChecking, setBulkChecking] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  // Check by Rekening state
  const [banks, setBanks] = useState([]);
  const [skipBanks, setSkipBanks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rekeningChecking, setRekeningChecking] = useState(false);
  const [rekeningProgress, setRekeningProgress] = useState(null);

  const isCheckingStatus = useRef(false);
  const isFetchingQR = useRef(false);
  const lastQRFetch = useRef(0); // ✅ NEW: Track last QR fetch time
  const statusInterval = useRef(null);
  const qrInterval = useRef(null);
  const connectionAttemptTime = useRef(null); // ✅ NEW: Track connection start time

  useEffect(() => {
    checkStatus();
    fetchBulkStats();
    fetchBanks();

    statusInterval.current = setInterval(() => {
      if (!isCheckingStatus.current) {
        checkStatus();
      }
    }, 5000);

    const statsInterval = setInterval(fetchBulkStats, 10000);

    return () => {
      if (statusInterval.current) clearInterval(statusInterval.current);
      if (qrInterval.current) clearInterval(qrInterval.current);
      clearInterval(statsInterval);
    };
  }, []);

  const checkStatus = async () => {
    if (isCheckingStatus.current) return;

    try {
      isCheckingStatus.current = true;
      const response = await axiosInstance.get(`/whatsapp/status`);
      const newStatus = response.data;

      setStatus(newStatus);

      // ✅ Check if stuck connecting for too long
      if (newStatus.isConnecting && connectionAttemptTime.current) {
        const elapsed = Date.now() - connectionAttemptTime.current;
        if (elapsed > 50000) { // 50 seconds
          console.warn('⚠️ Connection taking too long, might be stuck');
          setConnectionError('Koneksi memakan waktu terlalu lama. Coba Force Reset jika stuck.');
        }
      }

      if (newStatus.hasQR && !newStatus.isConnected && !qrCode && !isFetchingQR.current) {
        console.log('✅ Status: QR available, fetching...');
        fetchQR();
      }

      if (newStatus.isConnected) {
        setQrCode(null);
        setShowQRModal(false);
        setConnectionError(null);
        connectionAttemptTime.current = null;
        if (qrInterval.current) {
          clearInterval(qrInterval.current);
          qrInterval.current = null;
        }
        console.log('✅ Connected! Closing modal...');
      }

      // ✅ If not connecting and no connection, clear error
      if (!newStatus.isConnecting && !newStatus.isConnected) {
        connectionAttemptTime.current = null;
      }

    } catch (error) {
      console.error('Status check error:', error);
    } finally {
      isCheckingStatus.current = false;
    }
  };

  const fetchQR = async () => {
    if (isFetchingQR.current) return;
    
    // ✅ Throttle: Maximum 1 QR fetch per second
    const now = Date.now();
    if (now - lastQRFetch.current < 1000) {
      console.log('⏳ QR fetch throttled, waiting...');
      return;
    }
    
    lastQRFetch.current = now;

    try {
      isFetchingQR.current = true;
      console.log('📱 Fetching QR code...');

      const response = await axiosInstance.get(`/whatsapp/qr`);
      setQrCode(response.data.qr);
      setConnectionError(null); // Clear error on success
      console.log('✅ QR code received');

    } catch (error) {
      console.error('QR fetch error:', error);

      if (error.response?.status === 404) {
        console.log('⏳ QR not ready, retrying in 2s...');
        setTimeout(() => {
          isFetchingQR.current = false;
          lastQRFetch.current = 0; // Reset throttle on retry
          fetchQR();
        }, 2000);
      } else {
        setConnectionError('Gagal mendapatkan QR Code');
      }
    } finally {
      setTimeout(() => {
        isFetchingQR.current = false;
      }, 1000); // Keep locked for 1 second
    }
  };

  const fetchBulkStats = async () => {
    try {
      const response = await axiosInstance.get(`/whatsapp/bulk-stats`);
      setBulkStats(response.data);
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axiosInstance.get(`/whatsapp/banks`);
      setBanks(response.data.banks);
    } catch (error) {
      console.error('Fetch banks error:', error);
    }
  };

  const handleConnect = async () => {
    // ✅ Prevent multiple connect attempts
    if (status.isConnecting) {
      console.log('⚠️ Already connecting, ignoring request');
      return;
    }
    
    try {
      setLoading(true);
      setShowQRModal(true);
      setQrCode(null);
      setConnectionError(null);
      connectionAttemptTime.current = Date.now();
      lastQRFetch.current = 0; // Reset QR throttle

      console.log('📞 Requesting connection...');
      const response = await axiosInstance.post(`/whatsapp/connect`);

      if (response.data.success === false) {
        alert(response.data.message);
        setShowQRModal(false);
        connectionAttemptTime.current = null;
        return;
      }

      console.log('✅ Connection initiated');

      let attempts = 0;
      const maxAttempts = 25; // ✅ Increased timeout for slow connections

      qrInterval.current = setInterval(async () => {
        attempts++;

        if (attempts > maxAttempts) {
          clearInterval(qrInterval.current);
          setShowQRModal(false);
          setConnectionError('Timeout menunggu QR Code.');
          connectionAttemptTime.current = null;
          alert('Timeout menunggu QR Code. Gunakan tombol Force Reset jika masih stuck.');
          return;
        }

        await checkStatus();

      }, 2000);

    } catch (error) {
      console.error('Connect error:', error);
      
      // ✅ Handle 429 (already connecting)
      if (error.response?.status === 429) {
        setConnectionError('Connection already in progress');
      } else {
        setConnectionError('Gagal connect: ' + (error.response?.data?.error || error.message));
      }
      
      alert('Gagal connect: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
      setShowQRModal(false);
      connectionAttemptTime.current = null;
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Yakin ingin disconnect WhatsApp?')) return;

    try {
      setLoading(true);
      await axiosInstance.post(`/whatsapp/disconnect`);

      setQrCode(null);
      setStatus({ isConnected: false, hasQR: false, isConnecting: false });
      setShowQRModal(false);
      setConnectionError(null);
      connectionAttemptTime.current = null;

      alert('✅ Berhasil disconnect');

    } catch (error) {
      console.error('Disconnect error:', error);
      alert('Gagal disconnect: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Force reset handler
  const handleForceReset = async () => {
    if (!window.confirm('Force Reset akan membersihkan semua koneksi dan session WhatsApp.\n\nLanjutkan?')) {
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Force reset initiated...');

      const response = await axiosInstance.post(`/whatsapp/force-reset`);

      if (response.data.success) {
        setQrCode(null);
        setStatus({ isConnected: false, hasQR: false, isConnecting: false });
        setShowQRModal(false);
        setConnectionError(null);
        connectionAttemptTime.current = null;

        if (qrInterval.current) {
          clearInterval(qrInterval.current);
          qrInterval.current = null;
        }

        alert('✅ Force reset berhasil! Silakan connect ulang.');
      } else {
        alert('⚠️ Force reset gagal: ' + response.data.error);
      }

    } catch (error) {
      console.error('Force reset error:', error);
      alert('❌ Gagal force reset: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshQR = async () => {
    setQrCode(null);
    setConnectionError(null);
    isFetchingQR.current = false;
    lastQRFetch.current = 0; // ✅ Reset throttle
    await fetchQR();
  };

  const handleCheckNumber = async (e) => {
    e.preventDefault();

    if (!checkNumber.trim()) {
      alert('Masukkan nomor terlebih dahulu');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(`/whatsapp/check-number`, {
        nomor: checkNumber
      });
      setCheckResult(response.data);
    } catch (error) {
      console.error('Check number error:', error);
      alert('Gagal cek nomor: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCheck = async (limit) => {
    if (!window.confirm(`Yakin ingin cek ${limit} nomor dari database?`)) return;

    try {
      setBulkChecking(true);
      setBulkProgress({ current: 0, total: limit, status: 'processing' });

      console.log(`🔍 Starting bulk check for ${limit} numbers...`);

      const response = await axiosInstance.post(`/whatsapp/bulk-check`, { limit });

      setBulkProgress({
        current: response.data.checked,
        total: limit,
        valid: response.data.valid,
        invalid: response.data.invalid,
        status: 'complete'
      });

      await fetchBulkStats();

      alert(`✅ Selesai!\n\nDicek: ${response.data.checked}\nValid: ${response.data.valid}\nInvalid: ${response.data.invalid}`);

    } catch (error) {
      console.error('Bulk check error:', error);
      alert('Gagal bulk check: ' + (error.response?.data?.error || error.message));
      setBulkProgress(null);
    } finally {
      setBulkChecking(false);
    }
  };

  const handleBankToggle = (bank) => {
    if (skipBanks.includes(bank)) {
      setSkipBanks(skipBanks.filter(b => b !== bank));
    } else {
      setSkipBanks([...skipBanks, bank]);
    }
  };

  // ✅ Skip semua bank
  const handleCheckAllBanks = () => {
    setSkipBanks([...banks]);
  };

  // ✅ Hapus semua skip
  const handleUncheckAllBanks = () => {
    setSkipBanks([]);
  };

  const handleCheckByRekening = async (limit) => {
    if (!window.confirm(`Cek ${limit} data rekening sebagai nomor WhatsApp?\n\nStatus: ${statusFilter}\nSkip banks: ${skipBanks.length > 0 ? skipBanks.join(', ') : 'Tidak ada'}`)) {
      return;
    }

    try {
      setRekeningChecking(true);
      setRekeningProgress({ status: 'processing', total: limit });

      const response = await axiosInstance.post(`/whatsapp/check-by-rekening`, {
        skip_banks: skipBanks,
        status_filter: statusFilter,
        limit
      });

      setRekeningProgress({
        status: 'complete',
        checked: response.data.checked,
        valid_by_rek: response.data.valid_by_rek,
        invalid_by_rek: response.data.invalid_by_rek,
        from_invalid: response.data.from_invalid,
        from_valid: response.data.from_valid
      });

      await fetchBulkStats();

      alert(`✅ Selesai!\n\nDicek: ${response.data.checked}\nValid by Rek: ${response.data.valid_by_rek}\nInvalid by Rek: ${response.data.invalid_by_rek}\nDari Invalid: ${response.data.from_invalid}\nDari Valid: ${response.data.from_valid}`);

    } catch (error) {
      console.error('Check by rekening error:', error);
      alert('Gagal check by rekening: ' + (error.response?.data?.error || error.message));
      setRekeningProgress(null);
    } finally {
      setRekeningChecking(false);
    }
  };

  return (
    <div className="whatsapp-page">
      <div className="page-header">
        <h1>WhatsApp Connection</h1>
      </div>

      <div className="whatsapp-container">
        {/* Connection Card */}
        <div className="connection-card">
          <div className="status-indicator">
            {status.isConnected ? (
              <>
                <CheckCircle size={48} className="text-success" />
                <h2>Connected ✅</h2>
                <p>WhatsApp terhubung dan siap digunakan</p>
              </>
            ) : status.isConnecting ? (
              <>
                <RefreshCw size={48} className="text-warning rotating" />
                <h2>Connecting...</h2>
                <p>Menghubungkan ke WhatsApp</p>
                {connectionError && (
                  <div style={{ marginTop: '10px', padding: '10px', background: '#fff3cd', borderRadius: '5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertTriangle size={18} color="#856404" />
                    <span style={{ fontSize: '14px', color: '#856404' }}>{connectionError}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <XCircle size={48} className="text-danger" />
                <h2>Disconnected</h2>
                <p>WhatsApp belum terhubung</p>
              </>
            )}
          </div>

          <div className="connection-actions">
            {!status.isConnected && !status.isConnecting && (
              <button
                onClick={handleConnect}
                disabled={loading}
                className="btn btn-primary btn-large"
              >
                <Smartphone size={20} />
                {loading ? 'Connecting...' : 'Connect WhatsApp'}
              </button>
            )}

            {status.isConnected && (
              <button
                onClick={handleDisconnect}
                disabled={loading}
                className="btn btn-danger btn-large"
              >
                <LogOut size={20} />
                Disconnect
              </button>
            )}

            {/* ✅ NEW: Force Reset Button - show when connecting or has error */}
            {(status.isConnecting || connectionError) && (
              <button
                onClick={handleForceReset}
                disabled={loading}
                className="btn btn-warning btn-large"
                style={{ marginTop: '10px' }}
              >
                <AlertTriangle size={20} />
                Force Reset (Stuck?)
              </button>
            )}
          </div>
        </div>

        {/* Bulk Check Card */}
        {status.isConnected && (
          <div className="bulk-check-card">
            <div className="card-header">
              <Database size={24} />
              <h2>Bulk Check Nomor dari Database</h2>
            </div>

            <div className="bulk-stats">
              <div className="stat-item">
                <span className="stat-label">Belum Dicek</span>
                <span className="stat-value unchecked">{bulkStats.unchecked}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Valid</span>
                <span className="stat-value valid">{bulkStats.valid}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Invalid</span>
                <span className="stat-value invalid">{bulkStats.invalid}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total</span>
                <span className="stat-value total">{bulkStats.total}</span>
              </div>
            </div>

            <div className="bulk-actions">
              <button
                onClick={() => handleBulkCheck(10)}
                disabled={bulkChecking || bulkStats.unchecked === 0}
                className="btn btn-outline-primary"
              >
                Cek 10
              </button>
              <button
                onClick={() => handleBulkCheck(1000)}
                disabled={bulkChecking || bulkStats.unchecked === 0}
                className="btn btn-outline-primary"
              >
                Cek 1,000
              </button>
              <button
                onClick={() => handleBulkCheck(2000)}
                disabled={bulkChecking || bulkStats.unchecked === 0}
                className="btn btn-outline-primary"
              >
                Cek 2,000
              </button>
              <button
                onClick={() => handleBulkCheck(3000)}
                disabled={bulkChecking || bulkStats.unchecked === 0}
                className="btn btn-outline-primary"
              >
                Cek 3,000
              </button>
              <button
                onClick={() => handleBulkCheck(4000)}
                disabled={bulkChecking || bulkStats.unchecked === 0}
                className="btn btn-outline-primary"
              >
                Cek 4,000
              </button>
              <button
                onClick={() => handleBulkCheck(5000)}
                disabled={bulkChecking || bulkStats.unchecked === 0}
                className="btn btn-outline-primary"
              >
                Cek 5,000
              </button>
            </div>

            {bulkProgress && (
              <div className="bulk-progress">
                {bulkProgress.status === 'processing' ? (
                  <div className="progress-info">
                    <RefreshCw className="rotating" size={20} />
                    <span>Sedang mengecek {bulkProgress.total} nomor...</span>
                  </div>
                ) : (
                  <div className="progress-complete">
                    <CheckCircle size={20} className="text-success" />
                    <span>
                      Selesai! {bulkProgress.current} dicek
                      ({bulkProgress.valid} valid, {bulkProgress.invalid} invalid)
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Check by Rekening Card */}
        {status.isConnected && (
          <div className="bulk-check-card">
            <div className="card-header">
              <CreditCard size={24} />
              <h2>Check Rekening as WhatsApp Number</h2>
            </div>

            <p style={{ marginBottom: '20px', color: '#6b7280', fontSize: '14px' }}>
              Cek apakah nomor rekening di database adalah nomor WhatsApp yang aktif
            </p>

            {/* Status Filter */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                Cek dari data:
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="select"
                style={{ width: '100%' }}
              >
                <option value="all">Semua Data</option>
                <option value="invalid">Invalid Saja</option>
                <option value="valid">Valid Saja</option>
              </select>
            </div>

            {/* Skip Banks */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#374151' }}>
                Skip Bank (centang untuk skip):
              </label>

              {/* ✅ Control buttons + counter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleCheckAllBanks}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  Centang Semua
                </button>
                <button
                  type="button"
                  onClick={handleUncheckAllBanks}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  Hapus Centang
                </button>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  {skipBanks.length} dari {banks.length} bank di-skip
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                {banks.map(bank => (
                  <label key={bank} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={skipBanks.includes(bank)}
                      onChange={() => handleBankToggle(bank)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '14px', color: '#4b5563' }}>{bank}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bulk-actions">
              <button
                onClick={() => handleCheckByRekening(10)}
                disabled={rekeningChecking}
                className="btn btn-outline-primary"
              >
                Cek 10
              </button>
              <button
                onClick={() => handleCheckByRekening(1000)}
                disabled={rekeningChecking}
                className="btn btn-outline-primary"
              >
                Cek 1,000
              </button>
              <button
                onClick={() => handleCheckByRekening(2000)}
                disabled={rekeningChecking}
                className="btn btn-outline-primary"
              >
                Cek 2,000
              </button>
              <button
                onClick={() => handleCheckByRekening(3000)}
                disabled={rekeningChecking}
                className="btn btn-outline-primary"
              >
                Cek 3,000
              </button>
              <button
                onClick={() => handleCheckByRekening(4000)}
                disabled={rekeningChecking}
                className="btn btn-outline-primary"
              >
                Cek 4,000
              </button>
              <button
                onClick={() => handleCheckByRekening(5000)}
                disabled={rekeningChecking}
                className="btn btn-outline-primary"
              >
                Cek 5,000
              </button>
            </div>

            {/* Progress */}
            {rekeningProgress && (
              <div className="bulk-progress">
                {rekeningProgress.status === 'processing' ? (
                  <div className="progress-info">
                    <RefreshCw className="rotating" size={20} />
                    <span>Sedang check rekening sebagai nomor WA...</span>
                  </div>
                ) : (
                  <div className="progress-complete">
                    <CheckCircle size={20} className="text-success" />
                    <span>
                      Selesai! {rekeningProgress.checked} dicek
                      ({rekeningProgress.valid_by_rek} valid, {rekeningProgress.invalid_by_rek} invalid)
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Single Number Check */}
        {status.isConnected && (
          <div className="check-number-card">
            <h2>Cek Validitas Nomor</h2>
            <form onSubmit={handleCheckNumber}>
              <div className="form-group">
                <input
                  type="text"
                  value={checkNumber}
                  onChange={(e) => setCheckNumber(e.target.value)}
                  placeholder="Masukkan nomor WhatsApp (contoh: 08123456789)"
                  className="input"
                />
                <button type="submit" disabled={loading} className="btn btn-primary">
                  <Search size={18} />
                  {loading ? 'Checking...' : 'Cek Nomor'}
                </button>
              </div>
            </form>

            {checkResult && (
              <div className={`result-card ${checkResult.isValid ? 'success' : 'danger'}`}>
                {checkResult.isValid ? (
                  <>
                    <CheckCircle size={32} />
                    <h3>Nomor Valid ✅</h3>
                    <p>{checkResult.nomor} terdaftar di WhatsApp</p>
                  </>
                ) : (
                  <>
                    <XCircle size={32} />
                    <h3>Nomor Invalid ❌</h3>
                    <p>{checkResult.nomor} tidak terdaftar di WhatsApp</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="modal" style={{ display: 'flex' }}>
          <div className="modal-content qr-modal">
            <div className="modal-header">
              <h2>Scan QR Code WhatsApp</h2>
              <button
                onClick={() => {
                  setShowQRModal(false);
                  setConnectionError(null);
                  connectionAttemptTime.current = null;
                  if (qrInterval.current) clearInterval(qrInterval.current);
                }}
                className="btn-close"
                type="button"
              >
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {qrCode ? (
                <div className="qr-container">
                  <img src={qrCode} alt="QR Code" className="qr-code" />
                  <p className="qr-instruction">
                    <strong>Cara Scan:</strong><br/>
                    1. Buka WhatsApp di HP<br/>
                    2. Tap Menu (⋮) atau Settings<br/>
                    3. Pilih "Linked Devices"<br/>
                    4. Tap "Link a Device"<br/>
                    5. Scan QR code di atas
                  </p>
                  <button
                    onClick={handleRefreshQR}
                    className="btn btn-secondary"
                    type="button"
                    disabled={loading}
                  >
                    <RefreshCw size={18} />
                    Refresh QR
                  </button>
                </div>
              ) : (
                <div className="loading">
                  <div className="spinner"></div>
                  <p>Generating QR Code...</p>
                  <p className="text-muted">Tunggu sebentar, QR akan muncul...</p>
                  
                  {connectionError && (
                    <div style={{ marginTop: '20px', padding: '15px', background: '#fff3cd', borderRadius: '8px' }}>
                      <p style={{ color: '#856404', fontSize: '14px', margin: '0 0 10px 0' }}>
                        <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
                        {connectionError}
                      </p>
                      <button
                        onClick={handleForceReset}
                        className="btn btn-warning"
                        type="button"
                        disabled={loading}
                        style={{ width: '100%' }}
                      >
                        <AlertTriangle size={18} />
                        Force Reset Connection
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsAppConnect;